import { useMemo, useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { IconOrdering } from '@components/icons/ordering';

export const DataTable = observer(
  ({
    columns,
    data,
    minTableWidth = '1000px',
    onRowClick,
    rowWrap: RowWrap,
    tight = false,
    rowStyle = {},
    getRowProps = null,
    spacing = true,

    // Override sorting
    onSort,
    sortingType,
    sortingBy,
  }) => {
    const [{ sorting, order }, setSorting] = useState(() => {
      const defaultSortingCol = columns.find((col) => col.defaultSort !== undefined);
      if (defaultSortingCol) {
        return {
          sorting: defaultSortingCol.sortKey || defaultSortingCol.title,
          order: defaultSortingCol.defaultSort,
        };
      }
      return '';
    });
    const overrideSorting = onSort !== undefined;

    const columnCount = useMemo(
      () =>
        columns.reduce((sum, col) => {
          return sum + (col.width || 1);
        }, 0),
      [columns],
    );

    const sortBy = (title) => {
      if (sorting === title) {
        const newOrder = order === 'asc' ? 'desc' : 'asc';
        setSorting({ sorting: title, order: newOrder });
        onSort && onSort({ sorting: title, order: newOrder });
      } else {
        setSorting({ sorting: title, order });
        onSort && onSort({ sorting: title, order });
      }
    };

    const renderData = useMemo(() => {
      if (overrideSorting) return data;
      const sorted = data.slice();
      if (sorting) {
        const sorter = columns.find((col) => col.title === sorting).sorter;
        sorted.sort(sorter);
        if (order === 'desc') {
          sorted.reverse();
        }
        return sorted;
      }
      return data;
    }, [data, sorting, order, columns, overrideSorting]);

    return (
      <TableContainer minWidth={minTableWidth}>
        <TableHead spacing={spacing}>
          <TableHeadRow>
            {columns.map((col, colIndex) => {
              const canSort = col.sorter !== undefined || col.sortKey !== undefined;
              const onClick = () => (canSort ? sortBy(col.sortKey || col.title) : null);
              const isSortingBy =
                (overrideSorting && sortingBy === col.sortKey && sortingBy !== undefined) ||
                (sorting !== undefined && sorting === col.sortKey) ||
                sorting === col.title;

              return (
                <TableHeadColumn
                  isSortingBy={isSortingBy}
                  canSort={canSort}
                  key={colIndex}
                  colWidth={calculateWidthForColumn(col, columnCount)}
                  onClick={onClick}
                  style={col.style}
                >
                  <div>
                    {typeof col.title === 'function' ? col.title({ isSortingBy }) : col.title || ''}
                  </div>
                  {overrideSorting && (
                    <SortArrow
                      asc={sortingType === 'asc'}
                      visible={sortingBy === col.sortKey && sortingBy !== undefined}
                    />
                  )}
                  {!overrideSorting && canSort && (
                    <SortArrow
                      visible={
                        (sorting !== undefined && sorting === col.sortKey) || sorting === col.title
                      }
                      asc={order === 'asc'}
                    />
                  )}
                </TableHeadColumn>
              );
            })}
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {renderData.map((item, itemIndex) => {
            const row = (
              <TableBodyRow
                spacing={spacing}
                style={rowStyle}
                tight={tight}
                key={itemIndex}
                onClick={onRowClick ? () => onRowClick(item) : null}
              >
                {columns.map((col, colIndex) => (
                  <TableBodyColumn
                    key={colIndex}
                    colWidth={calculateWidthForColumn(col, columnCount)}
                    align={col.align}
                    style={col.style}
                  >
                    {col.render ? col.render(item) : col.dataKey ? item[col.dataKey] : '' || ''}
                  </TableBodyColumn>
                ))}
              </TableBodyRow>
            );
            if (!RowWrap) {
              return row;
            }
            return (
              <RowWrap item={item} key={itemIndex} {...(getRowProps && getRowProps(item))}>
                {row}
              </RowWrap>
            );
          })}
        </TableBody>
      </TableContainer>
    );
  },
);

const calculateWidthForColumn = (column, total) => {
  if (column.fixedWidth) return column.fixedWidth;
  return (((column.width || 1) / total) * 100).toString() + '%';
};

const TableContainer = styled.div((p) => ({
  width: '100%',
  minWidth: p.minWidth,
}));

const TableRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const TableBody = styled.div((p) => ({
  color: p.theme.colors.purple100,
  fontSize: 14,
}));

const TableHead = styled(({ spacing, ...rest }) => <div {...rest} />)((p) => ({
  padding: p.spacing ? '0 1rem' : 0,
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: p.theme.colors.white,
  borderBottom: '0.0625rem solid ' + p.theme.colors.purpleTrans10,
  [p.theme.mobileQuery]: {
    padding: '0 1.5rem',
  },
}));

const TableBodyRow = styled(({ spacing, ...rest }) => <TableRow {...rest} />)((p) => ({
  backgroundColor: '#fff',
  minHeight: '3.5rem',
  ...(p.onClick && { ':hover': { cursor: 'pointer' } }),
  borderBottom: '0.0625rem solid ' + p.theme.colors.purpleTrans10,
  padding: p.spacing ? '0 1rem' : 0,
  [p.theme.mobileQuery]: {
    padding: '0 1.5rem',
  },
  ':hover': {
    backgroundColor: p.theme.colors.blue5,
  },
}));

const TableHeadRow = styled(TableRow)((p) => ({
  ...p.theme.text.bold14,
  color: p.theme.colors.purple80,
  textTransform: 'uppercase',
  '> div': {
    padding: '1rem 0.5rem',
  },
}));

const TableColumn = styled.div((p) => ({
  minWidth: p.colWidth || '100%',
  width: p.colWidth || '100%',
  padding: '0 0.5rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const TableHeadColumn = styled(TableColumn)((p) => ({
  ...(p.canSort && {
    ':hover': { cursor: 'pointer', color: p.theme.colors.purple100 },
  }),
  display: 'flex',
  alignItems: 'center',
  ...(p.isSortingBy && {
    color: p.theme.colors.purple100,
  }),
}));
const TableBodyColumn = styled(TableColumn)((p) => ({
  textAlign: p.align || 'left',
}));

const SortArrow = styled(IconOrdering)((p) => ({
  visibility: p.visible ? 'visible' : 'hidden',
  color: p.theme.colors.purple50,

  ...(p.visible && { transition: 'all 200ms ease-out' }),
  ...(p.asc && { transform: 'rotate(180deg)' }),
}));
